import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ArrowRight, Settings, CheckCircle, Clock, Shield } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';

export function ZohoImplementation() {
  const process = [
    {
      step: "01",
      title: "Discovery & Planning",
      description: "We analyze your business processes and create a customized implementation roadmap for Zoho applications"
    },
    {
      step: "02", 
      title: "Configuration & Setup",
      description: "Expert configuration of Zoho CRM, Zoho One, Zoho Books, and other applications tailored to your needs"
    },
    {
      step: "03",
      title: "Data Migration",
      description: "Secure and seamless migration of your existing data to the Zoho platform with validation"
    },
    {
      step: "04",
      title: "Testing & Training",
      description: "Comprehensive testing and team training on Zoho applications to ensure smooth adoption"
    }
  ];

  const highlights = [
    {
      icon: <Clock className="h-6 w-6 text-green-600" />,
      title: "Quick Deployment",
      description: "Go live in 2-4 weeks"
    },
    {
      icon: <Shield className="h-6 w-6 text-green-600" />,
      title: "Data Security",
      description: "100% secure migration"
    },
    {
      icon: <CheckCircle className="h-6 w-6 text-green-600" />,
      title: "Success Guarantee",
      description: "Proven implementation process"
    }
  ];

  return (
    <section className="py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-green-100 text-green-800 rounded-full mb-4 text-sm lg:text-base">
            <Settings className="h-4 w-4 mr-2" />
            Implementation Services
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 lg:mb-6">
            Seamless Zoho Implementation
          </h2>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            From Zoho CRM to Zoho One, Zoho Books to Zoho Creator - we handle every aspect of your 
            Zoho implementation with minimal disruption to your business operations.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-16 lg:mb-20">
          <div className="space-y-6 lg:space-y-8 order-2 lg:order-1">
            <ImageWithFallback 
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop"
              alt="Team implementing Zoho solutions"
              className="rounded-2xl shadow-xl w-full h-[250px] sm:h-[300px] lg:h-[400px] object-cover"
            />
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
              {highlights.map((highlight, index) => (
                <Card key={index} className="text-center p-3 lg:p-4">
                  <CardContent className="p-0">
                    <div className="mb-3 flex justify-center">{highlight.icon}</div>
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm lg:text-base">{highlight.title}</h3>
                    <p className="text-xs text-gray-600">{highlight.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="space-y-6 lg:space-y-8 order-1 lg:order-2">
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900">Our Implementation Process</h3>
            
            <div className="space-y-6">
              {process.map((item, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 lg:w-14 lg:h-14 bg-green-200 text-green-700 rounded-full flex items-center justify-center font-extrabold text-lg lg:text-2xl shadow-md border-2 border-green-300">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-sm lg:text-base">{item.title}</h3>
                    <p className="text-gray-600 text-sm lg:text-base">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-green-50 rounded-xl p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm lg:text-base">Implementation Includes:</h3>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-700 text-sm lg:text-base">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-3 flex-shrink-0" />
                  Complete Zoho system configuration
                </li>
                <li className="flex items-center text-gray-700 text-sm lg:text-base">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-3 flex-shrink-0" />
                  Data migration and validation
                </li>
                <li className="flex items-center text-gray-700 text-sm lg:text-base">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-3 flex-shrink-0" />
                  User training on Zoho applications
                </li>
                <li className="flex items-center text-gray-700 text-sm lg:text-base">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-3 flex-shrink-0" />
                  Go-live support and monitoring
                </li>
              </ul>
            </div>

            <Button className="bg-black hover:bg-yellow-400 hover:text-black text-white w-full sm:w-auto text-base font-bold py-3 px-8 shadow-lg">
              Start Implementation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}